import { buildSharedSentryJsOptions } from "@membentry/shared-observability";

type WebSentryRuntime = "client" | "server" | "edge";

type WebObservabilityEnv = NodeJS.ProcessEnv;

interface BuildWebSentryOptionsBase {
  release?: string;
  releaseIdentity?: string;
  tags?: Record<string, string | null | undefined>;
}

type BuildWebSentryOptionsInput =
  | (BuildWebSentryOptionsBase & {
      runtime: Extract<WebSentryRuntime, "client">;
      env: WebObservabilityEnv;
    })
  | (BuildWebSentryOptionsBase & {
      runtime: Exclude<WebSentryRuntime, "client">;
      env?: WebObservabilityEnv;
    });

function resolveWebSentryDsn(env: WebObservabilityEnv): string | undefined {
  return env.SENTRY_DSN || env.NEXT_PUBLIC_SENTRY_DSN || undefined;
}

function resolveWebReleaseIdentity(env: WebObservabilityEnv): string | undefined {
  return (
    env.SENTRY_RELEASE_IDENTITY ||
    env.VERCEL_GIT_COMMIT_SHA ||
    env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA ||
    env.GITHUB_SHA ||
    undefined
  );
}

function resolveWebEnvironment(env: WebObservabilityEnv): string | undefined {
  return env.SENTRY_ENVIRONMENT || env.NODE_ENV;
}

export function buildWebSentryOptions(
  input: BuildWebSentryOptionsInput,
) {
  const env = input.env ?? process.env;

  return buildSharedSentryJsOptions({
    surface: "web",
    dsn: resolveWebSentryDsn(env),
    environment: resolveWebEnvironment(env),
    release: input.release ?? env.SENTRY_RELEASE,
    releaseIdentity: input.releaseIdentity ?? resolveWebReleaseIdentity(env),
    tags: {
      "runtime.target": input.runtime,
      ...input.tags,
    },
  });
}

export function buildWebVerificationSentryOptions(
  env: WebObservabilityEnv = process.env,
) {
  const baseOptions = buildWebSentryOptions({ runtime: "server", env });

  return {
    ...baseOptions,
    release: `${baseOptions.release}-manual-verify`,
    tags: {
      ...baseOptions.tags,
      "verification.surface": "web",
    },
  };
}
